import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/cart`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, user]);

  const addToCart = async (productId, quantity = 1) => {
    const token = localStorage.getItem('token');
    await axios.post(`${API_URL}/api/cart/add`, 
      { product_id: productId, quantity },
      { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      }
    );
    await fetchCart();
  };

  const updateQuantity = async (productId, quantity) => {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/api/cart/update`,
      { product_id: productId, quantity },
      { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      }
    );
    await fetchCart();
  };

  const removeFromCart = async (productId) => {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_URL}/api/cart/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
    await fetchCart();
  };

  const clearCart = async () => {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_URL}/api/cart`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
    await fetchCart();
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
    itemCount: cart.items.reduce((acc, item) => acc + item.quantity, 0)
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
